import React, { useState, useMemo } from 'react';
import { Product, InventoryCategory } from '../../types';
import { printAsExcel } from '../../utils/exportToExcel';
import { resolveCurrencyCode } from '../../utils/currencyUtils';
import {
  useInventoryCategories,
  useCreateInventoryCategory,
  useUpdateInventoryCategory,
  useDeleteInventoryCategory
} from '../../hooks/useInventoryCategories';
import { useProducts, useCreateProduct, useUpdateProduct, useDeleteProduct } from '../../hooks/useProducts';
import { useToast } from '../../contexts/ToastContext';
import ProductsTab from './ProductsTab';
import CategoriesTab from './CategoriesTab';
import {
  exportProductsCsv, exportCategoriesCsv,
  buildProductPrintData, buildCategoryPrintData,
  parseImportedProducts,
} from './exportHelpers';

const Inventory: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'products' | 'categories'>('products');
  const [showProductForm, setShowProductForm] = useState(false);
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | undefined>();
  const [editingCategory, setEditingCategory] = useState<InventoryCategory | undefined>();
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [stockLevelFilter, setStockLevelFilter] = useState('');
  const [currencyFilter, setCurrencyFilter] = useState('');
  const [categoryStatusFilter, setCategoryStatusFilter] = useState('');

  const { showToast } = useToast();

  const queryOptions = useMemo(() => ({
    sort: [{ field: 'createdAt', order: 'desc' as const }],
    pagination: { pageSize: 100 },
  }), []);

  const { data: categories = [], refetch: refetchCategories } = useInventoryCategories(queryOptions);
  const { data: products = [], refetch: refetchProducts } = useProducts(queryOptions);

  const createProductMutation = useCreateProduct(() => {
    refetchProducts(); setShowProductForm(false); setEditingProduct(undefined);
  });
  const updateProductMutation = useUpdateProduct(() => {
    refetchProducts(); setShowProductForm(false); setEditingProduct(undefined);
  });
  const deleteProductMutation = useDeleteProduct(() => { refetchProducts(); });

  const createCategoryMutation = useCreateInventoryCategory(() => {
    showToast('Category created successfully', 'success');
    refetchCategories(); setShowCategoryForm(false); setEditingCategory(undefined);
  });
  const updateCategoryMutation = useUpdateInventoryCategory(() => {
    showToast('Category updated successfully', 'success');
    refetchCategories(); setShowCategoryForm(false); setEditingCategory(undefined);
  });
  const deleteCategoryMutation = useDeleteInventoryCategory(() => {
    showToast('Category deleted successfully', 'success'); refetchCategories();
  });

  const handleAddProduct = async (data: Omit<Product, 'id' | 'createdAt' | 'updatedAt'>) => {
    try { await createProductMutation.execute(data); } catch (e) { console.error('Create product error:', e); }
  };

  const handleEditProduct = async (data: Omit<Product, 'id' | 'createdAt' | 'updatedAt'>) => {
    if (!editingProduct) return;
    try {
      await updateProductMutation.execute({
        id: editingProduct.id as any,
        data: { ...data, documentId: editingProduct.documentId }
      });
    } catch (e) { console.error('Update product error:', e); }
  };

  const handleDeleteProduct = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    const product = products.find(p => p.id === id);
    if (!product) return;
    try { await deleteProductMutation.execute(product as any); } catch (e) { console.error('Delete product error:', e); }
  };

  const handleAddCategory = async (data: Omit<InventoryCategory, 'id' | 'createdAt' | 'updatedAt'>) => {
    try { await createCategoryMutation.execute(data); } catch (e) { console.error('Create category error:', e); }
  };

  const handleEditCategory = async (data: Omit<InventoryCategory, 'id' | 'createdAt' | 'updatedAt'>) => {
    if (!editingCategory) return;
    try {
      await updateCategoryMutation.execute({ id: editingCategory.id, data: { ...editingCategory, ...data } });
    } catch (e) { console.error('Update category error:', e); }
  };

  const handleDeleteCategory = async (id: string) => {
    const cat = categories.find(c => c.id === id || c.documentId === id);
    // Product categoryId holds the category documentId (relation)
    if (products.some(p => p.categoryId === id || (cat?.documentId && p.categoryId === cat.documentId))) {
      showToast('Cannot delete category with existing products!', 'error'); return;
    }
    if (window.confirm('Are you sure you want to delete this category?')) {
      try {
        if (cat) await deleteCategoryMutation.execute(cat);
      } catch (e) { console.error('Delete category error:', e); }
    }
  };

  const handleImportProducts = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const imported = parseImportedProducts(e.target?.result as string, categories);
        for (const { id, createdAt, updatedAt, ...productData } of imported) {
          await createProductMutation.execute(productData);
        }
        refetchProducts();
        showToast(`Successfully imported ${imported.length} products!`, 'success');
      } catch (err) { showToast('Error importing file. Please check the format.', 'error'); console.error('Import error:', err); }
    };
    reader.readAsText(file);
  };

  const handleClearFilters = () => {
    setCategoryFilter(''); setStatusFilter(''); setStockLevelFilter('');
    setCurrencyFilter(''); setCategoryStatusFilter(''); setSearchTerm('');
  };

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (product.supplier && product.supplier.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = !categoryFilter || product.categoryId === categoryFilter;
    const matchesStatus = !statusFilter || (statusFilter === 'active' && product.isActive) || (statusFilter === 'inactive' && !product.isActive);
    const matchesStockLevel = !stockLevelFilter ||
      (stockLevelFilter === 'low' && product.quantity <= product.minStockLevel) ||
      (stockLevelFilter === 'normal' && product.quantity > product.minStockLevel && product.quantity < product.maxStockLevel) ||
      (stockLevelFilter === 'high' && product.quantity >= product.maxStockLevel);
    const matchesCurrency = !currencyFilter || resolveCurrencyCode(product.currency) === currencyFilter;
    return matchesSearch && matchesCategory && matchesStatus && matchesStockLevel && matchesCurrency;
  });

  const filteredCategories = categories.filter(category => {
    const matchesSearch = category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (category.description && category.description.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = !categoryStatusFilter ||
      (categoryStatusFilter === 'active' && category.isActive) || (categoryStatusFilter === 'inactive' && !category.isActive);
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Inventory Management</h1>
        <p className="text-gray-600 mt-1">Manage products, stock levels, and categories</p>
      </div>

      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => { setActiveTab('products'); setShowProductForm(false); setShowCategoryForm(false); }}
            className={`${activeTab === 'products' ? 'border-ocean-500 text-ocean-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Products ({products.length})
          </button>
          <button
            onClick={() => { setActiveTab('categories'); setShowProductForm(false); setShowCategoryForm(false); }}
            className={`${activeTab === 'categories' ? 'border-ocean-500 text-ocean-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Categories ({categories.length})
          </button>
        </nav>
      </div>

      {activeTab === 'products' && (
        <ProductsTab
          products={products} categories={categories} filteredProducts={filteredProducts}
          showProductForm={showProductForm} setShowProductForm={setShowProductForm}
          editingProduct={editingProduct} setEditingProduct={setEditingProduct}
          searchTerm={searchTerm} setSearchTerm={setSearchTerm}
          showFilters={showFilters} setShowFilters={setShowFilters}
          categoryFilter={categoryFilter} setCategoryFilter={setCategoryFilter}
          statusFilter={statusFilter} setStatusFilter={setStatusFilter}
          stockLevelFilter={stockLevelFilter} setStockLevelFilter={setStockLevelFilter}
          currencyFilter={currencyFilter} setCurrencyFilter={setCurrencyFilter}
          onAddProduct={handleAddProduct} onEditProduct={handleEditProduct}
          onDeleteProduct={handleDeleteProduct}
          onEditProductClick={(p) => { setEditingProduct(p); setShowProductForm(true); }}
          onExport={() => exportProductsCsv(filteredProducts, categories)}
          onPrint={() => printAsExcel(buildProductPrintData(filteredProducts, categories), 'Inventory Products')}
          onImport={handleImportProducts} onClearFilters={handleClearFilters}
        />
      )}

      {activeTab === 'categories' && (
        <CategoriesTab
          products={products} categories={categories} filteredCategories={filteredCategories}
          showCategoryForm={showCategoryForm} setShowCategoryForm={setShowCategoryForm}
          editingCategory={editingCategory} setEditingCategory={setEditingCategory}
          searchTerm={searchTerm} setSearchTerm={setSearchTerm}
          showFilters={showFilters} setShowFilters={setShowFilters}
          categoryStatusFilter={categoryStatusFilter} setCategoryStatusFilter={setCategoryStatusFilter}
          onAddCategory={handleAddCategory} onEditCategory={handleEditCategory}
          onDeleteCategory={handleDeleteCategory}
          onEditCategoryClick={(c) => { setEditingCategory(c); setShowCategoryForm(true); }}
          onExport={() => exportCategoriesCsv(filteredCategories, products)}
          onPrint={() => printAsExcel(buildCategoryPrintData(filteredCategories, products), 'Inventory Categories')}
          onClearFilters={handleClearFilters}
        />
      )}
    </div>
  );
};

export default Inventory;
